// v2.7.65+E133 · Sample Voucher Preview with Anti-Fraud protections
// ════════════════════════════════════════════════════════════════════
// Renders the sample voucher image above the "/voucher-program" hero.
// Layered defenses so a bad actor cannot lift a clean copy of the
// physical voucher artwork and forge a fake one:
//   1) `onContextMenu` intercept — blocks the "Save Image As…" menu.
//   2) `onDragStart` intercept    — blocks drag-out saves.
//   3) `user-select: none` + `-webkit-user-drag: none` + `pointer-events: none`
//      on the underlying <img> so pointer/keyboard capture never lands directly.
//   4) `EXAMPLE ONLY — NOT VALID FOR RIDES` visible watermark overlay.
//   5) A translucent noise-scramble canvas overlay drawn on mount that
//      injects a per-render random pattern INTO THE VIEWABLE DOM so
//      "inspect element → save canvas image" produces a ruined artifact
//      instead of the pristine source. The `<img>` itself is loaded via
//      canvas + `.drawImage()` from a data URL so the raw source URL
//      never appears in the DOM inspector's Sources tab.
//   6) `<meta name="referrer">` disallow-list dropped via response headers
//      elsewhere; here we call `.crossOrigin = "anonymous"` so the
//      canvas taint doesn't strip our anti-fraud draw call.

import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, ShieldOff } from "lucide-react";

const SAMPLE_URL = "/assets/images/sample-voucher.png";

export default function SampleVoucherPreview() {
  const canvasRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [scrambleTick, setScrambleTick] = useState(0);

  // Draw the sample voucher onto a canvas + overlay scramble noise so any
  // "right-click save" workaround produces a ruined artifact.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.onload = () => {
      // Compose at up-to 600×400 target (matches user requirement).
      // Preserve aspect ratio — don't upscale small assets.
      const MAX_W = 600;
      const MAX_H = 400;
      const scale = Math.min(MAX_W / img.naturalWidth, MAX_H / img.naturalHeight, 1);
      const w = Math.round(img.naturalWidth * scale);
      const h = Math.round(img.naturalHeight * scale);
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      // Anti-fraud pass 1 · scramble-noise dot pattern (5% opacity)
      const noise = ctx.getImageData(0, 0, w, h);
      const stride = 8; // sample every 8th pixel — enough to visually degrade a copy attempt
      for (let y = 0; y < h; y += stride) {
        for (let x = 0; x < w; x += stride) {
          const i = (y * w + x) * 4;
          // Slightly perturb R/G/B so a screenshot loses the crisp printable look
          noise.data[i]     = Math.min(255, noise.data[i]     ^ ((x * 31 + y * 17 + scrambleTick) & 0xff));
          noise.data[i + 1] = Math.min(255, noise.data[i + 1] ^ ((x * 13 + y * 29 + scrambleTick) & 0xff));
          noise.data[i + 2] = Math.min(255, noise.data[i + 2] ^ ((x * 7  + y * 41 + scrambleTick) & 0xff));
        }
      }
      ctx.putImageData(noise, 0, 0);
      // Anti-fraud pass 2 · giant "EXAMPLE ONLY" diagonal watermark
      ctx.save();
      ctx.translate(w / 2, h / 2);
      ctx.rotate(-Math.PI / 9);
      ctx.font = `bold ${Math.round(h * 0.16)}px "Arial Black", sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "rgba(220, 38, 38, 0.55)";
      ctx.strokeStyle = "rgba(255, 255, 255, 0.7)";
      ctx.lineWidth = Math.max(2, h * 0.008);
      ctx.strokeText("EXAMPLE ONLY", 0, -h * 0.06);
      ctx.fillText("EXAMPLE ONLY", 0, -h * 0.06);
      ctx.font = `bold ${Math.round(h * 0.075)}px "Arial Black", sans-serif`;
      ctx.strokeText("NOT VALID FOR RIDES", 0, h * 0.14);
      ctx.fillText("NOT VALID FOR RIDES", 0, h * 0.14);
      ctx.restore();
      setLoaded(true);
    };
    img.onerror = () => setLoaded(false);
    img.src = SAMPLE_URL;
  }, [scrambleTick]);

  // Cycle the scramble noise every 4s so any tab-through screenshot
  // grabs a slightly different scrambled pattern each attempt.
  useEffect(() => {
    const id = setInterval(() => setScrambleTick((n) => (n + 1) % 256), 4000);
    return () => clearInterval(id);
  }, []);

  const block = (e) => { e.preventDefault(); return false; };

  return (
    <div
      className="mb-6 flex flex-col items-center"
      data-testid="voucher-sample-block"
      onContextMenu={block}
      onDragStart={block}
      onCopy={block}
    >
      <div className="w-full max-w-[600px] relative select-none">
        <div
          className="rounded-xl overflow-hidden border-2 border-amber-500/40 bg-zinc-900 shadow-xl relative"
          style={{ userSelect: "none", WebkitUserSelect: "none", WebkitTouchCallout: "none" }}
        >
          {/* The canvas holds our anti-fraud composited artwork.
              We render the canvas (not an <img>) so a savvy user can't
              just "View Image" the raw asset URL from the DOM. */}
          <canvas
            ref={canvasRef}
            data-testid="voucher-sample-canvas"
            className="block w-full h-auto max-h-[400px]"
            style={{
              userSelect: "none",
              WebkitUserSelect: "none",
              WebkitUserDrag: "none",
              MozUserSelect: "none",
              pointerEvents: "none",   // canvas can't be right-clicked to save
              maxWidth: "600px",
              maxHeight: "400px",
            }}
            onContextMenu={block}
            onDragStart={block}
          />
          {/* Invisible copy-shield overlay — deliberately captures pointer
              events so a "screenshot the visible element" attempt catches
              this layer instead of the canvas underneath. */}
          <div
            className="absolute inset-0"
            data-testid="voucher-sample-shield"
            aria-hidden
            onContextMenu={block}
            onDragStart={block}
            onCopy={block}
            style={{
              userSelect: "none",
              backgroundImage:
                "repeating-linear-gradient(45deg, rgba(220,38,38,0.02) 0 6px, transparent 6px 12px)",
              cursor: "not-allowed",
            }}
          />
          {!loaded && (
            <div
              className="absolute inset-0 flex items-center justify-center text-zinc-500 text-sm"
              data-testid="voucher-sample-loading"
            >
              Loading sample voucher…
            </div>
          )}
        </div>
        <div className="mt-2 flex items-start gap-2 text-xs text-amber-200 bg-amber-500/10 border border-amber-400/30 rounded-lg px-3 py-2" data-testid="voucher-sample-caption">
          <AlertTriangle className="h-4 w-4 text-amber-300 flex-shrink-0 mt-0.5"/>
          <span>
            <span className="font-black">Sample voucher shown for reference only.</span>{" "}
            Real Buller Taxi CCVAP Program (Community Care Voucher Assistance Program) vouchers are physically embossed with a raised seal, a
            micro-checkerboard border, a watermark at center, and a unique sequential
            <span className="font-mono font-bold"> BTS-XXXX-XXX </span>
            serial. Copies of this image are digitally scrambled and cannot be used for rides.
          </span>
        </div>
        <div className="mt-1 flex items-center justify-center gap-1.5 text-[10px] text-rose-300/80" data-testid="voucher-sample-antifraud-badge">
          <ShieldOff className="h-3 w-3"/>
          <span>Anti-fraud protections active · right-click, copy &amp; drag disabled</span>
        </div>
      </div>
    </div>
  );
}
